import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import TestModeModal from './TestModeModal';
import './Register.css';

function Register() {

  const [searchParams] = useSearchParams();
  // Получаем реферальный код из URL или sessionStorage
  const refFromUrl = searchParams.get('ref');
  const refCode = refFromUrl || sessionStorage.getItem('referral_code');
  
  // Сохраняем ref в sessionStorage, если он есть в URL
  useEffect(() => {
    if (refFromUrl) {
      sessionStorage.setItem('referral_code', refFromUrl);
    }
  }, [refFromUrl]);
  const [vkAuthUrl, setVkAuthUrl] = useState(null);
  const [yandexAuthUrl, setYandexAuthUrl] = useState(null);
  const [mailruAuthUrl, setMailruAuthUrl] = useState(null);
  const [personalDataConsent, setPersonalDataConsent] = useState(false);
  const [personalDataConsentShake, setPersonalDataConsentShake] = useState(false);
  const [vkAvailable, setVkAvailable] = useState(false);
  const [yandexAvailable, setYandexAvailable] = useState(false);
  const [mailruAvailable, setMailruAvailable] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const detectProviders = async () => {
      const check = async (url, setter) => {
        try {
          await axios.get(url, { params: { terms_accepted: false } });
          setter(true);
        } catch (e) {
          if (e.response?.status === 400) {
            setter(true);
          } else {
            setter(false);
          }
        }
      };

      await Promise.all([
        check('/api/vk-auth/login', setVkAvailable),
        check('/api/yandex-auth/login', setYandexAvailable),
        check('/api/mailru-auth/login', setMailruAvailable),
      ]);
    };

    detectProviders();
  }, []);

  useEffect(() => {
    // Проверяем ошибки от OAuth провайдеров
    const error = searchParams.get('error');
    if (error) {
      const errorMessages = {
        'invalid_state': 'Ошибка безопасности. Попробуйте снова.',
        'token_exchange_failed': 'Не удалось получить токен.',
        'invalid_token': 'Недействительный токен.',
        'email_not_verified': 'Email не подтвержден.',
        'email_not_provided': 'Email не предоставлен провайдером.',
        'no_user_id': 'Не удалось получить ID пользователя.',
        'server_error': 'Ошибка сервера. Попробуйте позже.',
        'vk_error': 'Ошибка авторизации через VK. Проверьте настройки приложения.',
        'vk_access_denied': 'Доступ запрещен. Вы отменили авторизацию через VK.',
        'vk_invalid_client': 'Ошибка конфигурации VK. Обратитесь к администратору.',
        'yandex_error': 'Ошибка авторизации через Yandex.',
        'mailru_error': 'Ошибка авторизации через Mail.ru. Проверьте настройки приложения.'
      };
      toast.error(errorMessages[error] || 'Ошибка авторизации');
    }

    // Получаем URL для OAuth провайдеров только после принятия условий соглашения
    if (personalDataConsent) {
      fetchVkAuthUrl();
      fetchYandexAuthUrl();
      fetchMailruAuthUrl();
    } else {
      setVkAuthUrl(null);
      setYandexAuthUrl(null);
      setMailruAuthUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, personalDataConsent]);

  const fetchVkAuthUrl = async () => {
    try {
      const response = await axios.get('/api/vk-auth/login', {
        params: { terms_accepted: personalDataConsent, ref: refCode }
      });
      setVkAuthUrl(response.data.auth_url);
    } catch (error) {
      console.log('VK OAuth не настроен:', error);
    }
  };

  const fetchYandexAuthUrl = async () => {
    try {
      const response = await axios.get('/api/yandex-auth/login', {
        params: { terms_accepted: personalDataConsent, ref: refCode }
      });
      setYandexAuthUrl(response.data.auth_url);
    } catch (error) {
      console.log('Yandex OAuth не настроен:', error);
    }
  };

  const fetchMailruAuthUrl = async () => {
    try {
      const response = await axios.get('/api/mailru-auth/login', {
        params: { terms_accepted: personalDataConsent, ref: refCode }
      });
      setMailruAuthUrl(response.data.auth_url);
    } catch (error) {
      console.log('Mail.ru OAuth не настроен:', error);
    }
  };

  const guardPersonalDataConsent = () => {
    setPersonalDataConsentShake(true);
    setTimeout(() => setPersonalDataConsentShake(false), 600);
    toast.error('Необходимо дать согласие на обработку персональных данных');
  };

  // Если пользователь уже авторизован, перенаправляем на главную
  if (user) {
    window.location.href = '/home';
    return null;
  }

  return (
    <div className="register-container">
      <div className="register-left">
        <div className="register-card">
          {/* Иконка */}
          <div className="register-icon">
            <img src="/logo.png" alt="Pikly" />
          </div>

          {/* Приветствие */}
          <div className="register-welcome">
            <h1>Добро пожаловать в PIKLY!</h1>
            <p>Зарегистрируйтесь и начните творить вместе с нами</p>
          </div>

          {/* OAuth кнопки */}
          <div className="oauth-buttons">
            {yandexAvailable && (
              <button
                type="button"
                className={`oauth-btn yandex-btn ${!personalDataConsent ? 'oauth-disabled' : ''}`}
                onClick={async () => {
                  if (!personalDataConsent) return guardPersonalDataConsent();
                  const url = yandexAuthUrl || (await (async () => {
                    try {
                      const r = await axios.get('/api/yandex-auth/login', { params: { terms_accepted: true, ref: refCode } });
                      return r.data.auth_url;
                    } catch {
                      return null;
                    }
                  })());
                  if (url) {
                    if (!yandexAuthUrl) setYandexAuthUrl(url);
                    window.location.href = url;
                  } else {
                    toast.error('Yandex авторизация недоступна');
                  }
                }}
              >
                <svg width="360" height="44" viewBox="0 0 360 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 18C0 8.05887 0 3.08827 2.54411 0.544106C5.08827 0 8.05887 0 18 0H342C351.941 0 354.912 0 357.456 0.544106C360 3.08827 360 8.05887 360 18V26C360 35.9411 360 38.9117 357.456 41.4559C354.912 44 351.941 44 342 44H18C8.05887 44 5.08827 44 2.54411 41.4559C0 38.9117 0 35.9411 0 26V18Z" fill="black"></path>
                  <g transform="translate(38.91, 8.91)">
                    <rect width="26.18" height="26.18" rx="13.09" fill="#FC3F1D"></rect>
                    <text x="13.09" y="16.5" fill="white" fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" fontSize="14" fontWeight="700" textAnchor="middle" dominantBaseline="middle">Я</text>
                  </g>
                  <text x="170" y="22" fill="white" fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" fontSize="14" fontWeight="500" textAnchor="middle" dominantBaseline="middle">Войти с Яндекс ID</text>
                </svg>
              </button>
            )}

            {vkAvailable && (
              <button
                type="button"
                className={`oauth-btn vk-btn ${!personalDataConsent ? 'oauth-disabled' : ''}`}
                onClick={async () => {
                  if (!personalDataConsent) return guardPersonalDataConsent();
                  const url = vkAuthUrl || (await (async () => {
                    try {
                      const r = await axios.get('/api/vk-auth/login', { params: { terms_accepted: true, ref: refCode } });
                      return r.data.auth_url;
                    } catch {
                      return null;
                    }
                  })());
                  if (url) {
                    if (!vkAuthUrl) setVkAuthUrl(url);
                    window.location.href = url;
                  } else {
                    toast.error('VK авторизация недоступна');
                  }
                }}
              >
                <svg width="360" height="44" viewBox="0 0 360 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 18C0 8.05887 0 3.08827 2.54411 0.544106C5.08827 0 8.05887 0 18 0H342C351.941 0 354.912 0 357.456 0.544106C360 3.08827 360 8.05887 360 18V26C360 35.9411 360 38.9117 357.456 41.4559C354.912 44 351.941 44 342 44H18C8.05887 44 5.08827 44 2.54411 41.4559C0 38.9117 0 35.9411 0 26V18Z" fill="#0077FF"></path>
                  <g transform="translate(38.91, 8.91)">
                    <svg width="26.18" height="26.18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" clipRule="evenodd" d="M4.2653 4.2653C3 5.5306 3 7.56707 3 11.64V12.36C3 16.4329 3 18.4694 4.2653 19.7347C5.5306 21 7.56706 21 11.64 21H12.36C16.4329 21 18.4694 21 19.7347 19.7347C21 18.4694 21 16.4329 21 12.36V11.64C21 7.56707 21 5.5306 19.7347 4.2653C18.4694 3 16.4329 3 12.36 3H11.64C7.56706 3 5.5306 3 4.2653 4.2653Z" fill="white"></path>
                      <path d="M12.6095 16C8.55576 16 6.09636 13.1823 6 8.5H8.05309C8.1171 11.9395 9.67903 13.397 10.8764 13.6967V8.5H12.8439V11.4683C13.9988 11.3401 15.2076 9.98991 15.614 8.5H17.5505C17.2406 10.3321 15.9246 11.6823 14.9948 12.2392C15.9253 12.6895 17.4225 13.8682 18 16H15.8714C15.4219 14.5749 14.321 13.4712 12.8446 13.3213V16H12.6095Z" fill="#0077FF"></path>
                    </svg>
                  </g>
                  <text x="170" y="22" fill="white" fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" fontSize="14" fontWeight="500" textAnchor="middle" dominantBaseline="middle">Войти с VK ID</text>
                </svg>
              </button>
            )}

            {mailruAvailable && (
              <button
                type="button"
                className={`oauth-btn mailru-btn ${!personalDataConsent ? 'oauth-disabled' : ''}`}
                onClick={async () => {
                  if (!personalDataConsent) return guardPersonalDataConsent();
                  const url = mailruAuthUrl || (await (async () => {
                    try {
                      const r = await axios.get('/api/mailru-auth/login', { params: { terms_accepted: true, ref: refCode } });
                      return r.data.auth_url;
                    } catch {
                      return null;
                    }
                  })());
                  if (url) {
                    if (!mailruAuthUrl) setMailruAuthUrl(url);
                    window.location.href = url;
                  } else {
                    toast.error('Mail.ru авторизация недоступна');
                  }
                }}
              >
                <svg width="360" height="44" viewBox="0 0 360 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 18C0 8.05887 0 3.08827 2.54411 0.544106C5.08827 0 8.05887 0 18 0H342C351.941 0 354.912 0 357.456 0.544106C360 3.08827 360 8.05887 360 18V26C360 35.9411 360 38.9117 357.456 41.4559C354.912 44 351.941 44 342 44H18C8.05887 44 5.08827 44 2.54411 41.4559C0 38.9117 0 35.9411 0 26V18Z" fill="#005FF9"></path>
                  <g transform="translate(38.91, 8.91)">
                    <svg width="26.18" height="26.18" viewBox="0 0 1000 1000" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fill="#0077FF" d="M267.1391907,0.4175824h466.6666565c147.276001,0,266.666687,119.3907242,266.666687,266.666687v466.6666565  c0,147.2759399-119.390686,266.666687-266.666687,266.666687H267.1391907  c-147.2759399,0-266.6666565-119.3907471-266.6666565-266.666687V267.084259  C0.4725275,119.8083038,119.8632507,0.4175824,267.1391907,0.4175824z"/>
                      <path fill="#FFFFFF" d="M500.4725342,122.639801c208.1766968,0,377.777771,169.6011047,377.777771,377.777771  c0,129.6944885-60.5244141,190.8844299-137.0111084,190.8844299c-44.5610962,0-83.1377563-23.2788696-105.0855713-57.8643799  c-34.5855713,35.2510986-82.4733276,57.1988525-135.6810913,57.1988525  c-105.0855408,0-190.2189026-85.1333618-190.2189026-190.2189026S395.3869934,310.1987,500.4725342,310.1987  s190.2188721,85.1333313,190.2188721,190.2188721v61.8544617c0,31.2599487,21.9489136,53.2077637,50.5477905,53.2077637  c33.9200439,0,61.8544312-28.598877,61.8544312-115.0622253c0-166.9399719-135.6810913-302.6210938-302.6210938-302.6210938  S197.8514099,333.4776001,197.8514099,500.417572s135.6811218,302.6211243,302.6211243,302.6211243  c96.4400024,0,162.9500122-47.8866577,162.9500122-47.8866577l49.8822021,57.1977539  c0,0-85.1322021,65.8455811-212.8322144,65.8455811c-208.1766968,0-377.777771-169.6011353-377.777771-377.7778015  S292.2958374,122.639801,500.4725342,122.639801z M385.4103088,500.417572  c0,63.1844788,51.2122498,115.0622253,115.0622253,115.0622253c63.1844482,0,115.0621948-51.8777466,115.0621948-115.0622253  c0-63.8499756-51.8777466-115.0621948-115.0621948-115.0621948  C436.6225586,385.3553772,385.4103088,436.5675964,385.4103088,500.417572z"/>
                    </svg>
                  </g>
                  <text x="170" y="22" fill="white" fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" fontSize="14" fontWeight="500" textAnchor="middle" dominantBaseline="middle">Войти с Mail.ru</text>
                </svg>
              </button>
            )}
          </div>

          {/* Согласие на обработку персональных данных и пользовательское соглашение */}
          <div className={`form-group terms-group personal-data-consent ${personalDataConsentShake ? 'shake-animation' : ''}`}>
            <label className="terms-checkbox-label">
              <input
                className="terms-checkbox"
                type="checkbox"
                checked={personalDataConsent}
                onChange={(e) => setPersonalDataConsent(e.target.checked)}
              />
              <span className="terms-text">
                Я даю согласие на обработку персональных данных в соответствии с{' '}
                <a
                  href="/policy_processing_personal_data.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="terms-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  политикой в отношении обработки персональных данных
                </a>
                {' '}и принимаю{' '}
                <a
                  href="/user_agreement.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="terms-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  пользовательское соглашение
                </a>
              </span>
            </label>
          </div>

          {/* Ссылка на вход */}
          <div className="register-footer">
            <p>
              Уже есть аккаунт?{' '}
              <a href={refCode ? `/login?ref=${refCode}` : '/login'} className="register-link">
                Войти
              </a>
            </p>
          </div>
        </div>
      </div>
      <div className="register-right">
        <div className="register-wallpaper">
          <img src="/wallpaper1.jpg" alt="Nature" />
        </div>
      </div>
      <TestModeModal storageKey="testModeModalSeenRegister" />
    </div>
  );
}

export default Register;

